import React, { useRef, useState, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import ChannelStatic from './ChannelStatic';
import { useAuth } from '../context/AuthContext';
import { useChannels } from '../context/CategoriesContext';

function playClick() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;

    // Sharp attack pop (channel-change click) via band-passed noise + a thud.
    const bufferSize = Math.floor(ctx.sampleRate * 0.06);
    const noiseBuf = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = noiseBuf.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize);
    const noise = ctx.createBufferSource();
    noise.buffer = noiseBuf;
    const noiseFilter = ctx.createBiquadFilter();
    noiseFilter.type = 'bandpass';
    noiseFilter.frequency.value = 2200;
    noiseFilter.Q.value = 0.8;
    const noiseGain = ctx.createGain();
    noiseGain.gain.setValueAtTime(0.35, now);
    noiseGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.06);
    noise.connect(noiseFilter).connect(noiseGain).connect(ctx.destination);
    noise.start(now);
    noise.stop(now + 0.07);

    // Low thud for the knob "ka-chunk" body
    const osc = ctx.createOscillator();
    osc.type = 'square';
    osc.frequency.setValueAtTime(160, now);
    osc.frequency.exponentialRampToValueAtTime(60, now + 0.05);
    const oscGain = ctx.createGain();
    oscGain.gain.setValueAtTime(0.18, now);
    oscGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.08);
    osc.connect(oscGain).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.09);

    setTimeout(() => ctx.close(), 200);
  } catch {
    /* ignore audio failures silently */
  }
}

export default function Layout({ children }) {
  // PWR LED toggles between green (on) and amber (standby). Starts green.
  const [isAmber, setIsAmber] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const screenRef = useRef(null);
  const [knobRotation, setKnobRotation] = useState(0);
  const isHome = location.pathname === '/';
  const isFit = isHome || location.pathname === '/about';
  const { user } = useAuth();
  const channelOrder = useChannels().map((c) => c.path);

  const advanceChannel = useCallback(() => {
    // Admin-only routes don't map, so treat those as "home".
    const current = channelOrder.indexOf(location.pathname);
    const next = channelOrder[(current + 1) % channelOrder.length];

    playClick();
    setKnobRotation((r) => r + 45);

    // Flicker the screen, then navigate mid-flicker for that VHS channel-flip feel.
    const el = screenRef.current;
    if (el) {
      el.classList.remove('channel-flicker');
      // Force reflow so the animation restarts even on rapid clicks.
      void el.offsetWidth;
      el.classList.add('channel-flicker');
    }
    setTimeout(() => navigate(next), 150);
    setTimeout(() => { if (screenRef.current) screenRef.current.classList.remove('channel-flicker'); }, 560);
  }, [location.pathname, navigate, channelOrder]);

  return (
    <div className="h-screen w-full bg-[#1a120a] pt-0 pb-1 px-1 md:pt-0 md:pb-1 md:px-1 overflow-hidden flex">
      <div className="site-tv w-full relative flex flex-col flex-1 min-h-0">
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-30">
          {user?.is_admin ? (
            <Link
              to="/admin"
              data-testid="bezel-brand-admin-link"
              className="bezel-brand hover:text-[#3a2512] transition-colors"
              title="Go to admin dashboard"
            >
              DILLEY · VISION
            </Link>
          ) : (
            <span className="bezel-brand">DILLEY · VISION</span>
          )}
        </div>

        <div className="site-tv-screen flex-1 flex flex-col min-h-0">
          <div ref={screenRef} id="tv-screen-root" className="flex-1 flex flex-col min-h-0 relative">
            <Header />
            <main className={`relative flex-1 min-h-0 ${isFit ? 'overflow-hidden flex flex-col items-center justify-center' : 'overflow-y-auto'}`}>{children}</main>
            <Footer />
            <ChannelStatic />
            <div className="screen-scanlines" />
            <div className="screen-vignette" />
          </div>
        </div>

        <div className="site-tv-controls">
          <button
            type="button"
            onClick={() => setIsAmber((v) => !v)}
            aria-pressed={isAmber}
            aria-label={isAmber ? 'Power LED amber — click to switch to green' : 'Power LED green — click to switch to amber'}
            data-testid="bezel-power-toggle"
            className="flex items-center gap-2 bg-transparent border-0 p-0 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#f5a623] rounded"
          >
            <span className={`bezel-led ${isAmber ? 'led-amber' : ''}`} />
            <span className="font-crt text-[11px] tracking-widest text-[#5c3f18]">PWR</span>
          </button>
          <div className="bezel-speaker" aria-hidden />
          <div className="flex items-center gap-4">
            <div className="flex flex-col items-center gap-1">
              <div className="bezel-knob" />
              <span className="font-crt text-[9px] tracking-widest text-[#5c3f18]">VOL</span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <button
                type="button"
                onClick={advanceChannel}
                aria-label="Change channel"
                data-testid="bezel-ch-knob"
                className="bezel-knob-btn"
                style={{ transform: `rotate(${knobRotation}deg)` }}
              >
                <span className="bezel-knob block" />
              </button>
              <span className="font-crt text-[9px] tracking-widest text-[#5c3f18]">CH</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
